/**
 * Y-Axis component for price/volume tick marks and labels.
 * Subscribes to 'scales' event for updates.
 *
 * Uses core/tickGeneration for pure tick calculation logic.
 * This component is a thin visual wrapper.
 */

import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  formatPrice,
  formatVolume,
  generateNiceYTicks,
  getFirstSeriesCurrency,
} from "~utils/ticker-chart";
import { useChartSubscription } from "~hooks/ticker-chart";
import type { YAxisPosition } from "~types/types";

export interface YAxisProps {
  /** Axis position (left or right) */
  position?: YAxisPosition;
  /** Type of axis (price or volume) */
  type?: "price" | "volume";
  /** Number of target ticks (actual count may vary) */
  tickCount?: number;
  /** Custom formatter function */
  formatter?: (value: number, currency?: string) => string;
  /** Locale for number formatting */
  locale?: string;
  /** Threshold for compact notation (e.g., 1K, 1M), default: 10000 */
  compactThreshold?: number;
}

/**
 * Y-Axis renders price or volume tick marks and labels.
 * Updates imperatively when scales change.
 */
const YAxisInner: React.FC<YAxisProps> = ({
  position = "right",
  type = "price",
  tickCount = 6,
  formatter,
  locale = "en-US",
  compactThreshold = 10000,
}) => {
  const store = useChartContext();

  useChartSubscription(store, "scales");

  const state = store.getState();
  const layout = store.getLayout();
  const scales = store.getScales();

  const scale = type === "volume" && scales.volume ? scales.volume : scales.y;

  // Determine if in comparison mode (no currency shown)
  const isComparisonMode = state.mode === "comparison";

  // Get currency from first series
  const seriesCurrency = getFirstSeriesCurrency(state.seriesMeta);

  // In comparison mode, don't show currency symbol
  const displayCurrency = isComparisonMode ? undefined : seriesCurrency;

  const ticks = useMemo(() => {
    const domain = scale.domain();
    const rawTicks = generateNiceYTicks(domain, tickCount);
    const [min, max] = domain;

    // Use compact notation for large values (1K, 1M, 1B)
    const useCompact =
      Math.abs(max) >= compactThreshold || Math.abs(min) >= compactThreshold;

    let formatFn: (val: number) => string;

    if (formatter) {
      formatFn = (val: number) => formatter(val, displayCurrency);
    } else if (type === "volume") {
      formatFn = (val: number) => formatVolume(val, locale);
    } else {
      formatFn = (val: number) =>
        formatPrice(val, {
          currency: displayCurrency,
          locale,
          compact: useCompact,
        });
    }

    const [rangeBottom, rangeTop] = scale.range();
    const edgePadding = layout.typography.axisLabelSize;

    return rawTicks
      .map((value) => ({
        value,
        y: scale.scale(value),
        label: formatFn(value),
      }))
      .filter(
        ({ y }) => y >= rangeTop + edgePadding && y <= rangeBottom - edgePadding
      );
  }, [
    scale,
    tickCount,
    formatter,
    type,
    displayCurrency,
    locale,
    compactThreshold,
    layout.typography.axisLabelSize,
  ]);

  const chartArea =
    type === "volume" && layout.volumeArea
      ? layout.volumeArea
      : layout.priceArea;

  // Get typography from layout (scales with chart size)
  const { axisLabelSize } = layout.typography;

  // Labels positioned close to the chart boundary with minimal padding (4px)
  const labelX =
    position === "right" ? chartArea.x + chartArea.width + 4 : chartArea.x - 4;
  const textAnchor = position === "right" ? "start" : "end";

  // Tick line extends across the chart area as grid lines
  const tickX1 = chartArea.x;
  const tickX2 = chartArea.x + chartArea.width;

  return (
    <g className={styles.yAxis} data-component="price-movement-chart-yaxis">
      {/* Tick marks and labels */}
      {ticks.map(({ value, y, label }) => (
        <g key={value} transform={`translate(0, ${y})`}>
          {/* Grid line - dashed horizontal line across chart */}
          <line
            className={styles.gridLine}
            x1={tickX1}
            x2={tickX2}
            data-component="price-movement-chart-yaxis-grid-line"
          />

          {/* Tick label */}
          <text
            className={styles.axisTick}
            x={labelX}
            dy="0.32em"
            textAnchor={textAnchor}
            fontSize={axisLabelSize}
            data-component="price-movement-chart-yaxis-label"
          >
            {label}
          </text>
        </g>
      ))}
    </g>
  );
};

export const YAxis = React.memo(YAxisInner);
YAxis.displayName = "YAxis";
